import { useEffect, useState, useMemo } from "react";
import { Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, Activity, Sun, Moon, Users, AlertCircle, Cpu, Zap, Database } from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { scaleLinear } from "d3-scale";
import { useApp } from "@/context/AppCtx";

const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

interface ProxyMetricsData {
  summary: { total: number; avgResponseTime: number };
  historyBuckets?: any[];
  mapData?: any[];
  activeUsers?: number;
  errorRate?: number;
  modelsTraffic?: { model: string, reqs: number }[];
  backendAnalysis?: { cycles: number, tokensAnalyzed: number, batchSize: number, isRunning: boolean };
  advancedAnalytics?: any[];
}

function formatNumber(num: number) {
  if (num >= 1000) return (num / 1000).toFixed(2) + 'k';
  return num.toString();
}

export default function ProxyMetricsPage() {
  const [data, setData] = useState<ProxyMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("1h");
  const [page, setPage] = useState(0);
  const { theme, toggleTheme } = useApp();

  const maxCount = useMemo(() => {
    if (!data?.mapData) return 0;
    return Math.max(...data.mapData.map(m => m.count), 1);
  }, [data?.mapData]);


  const fetchMetrics = async () => {
    try {
      const res = await fetch(`/api/proxy-metrics?range=${encodeURIComponent(timeRange)}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error("Failed to fetch proxy metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 3000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const countries = useMemo(() => {
    if (!data?.mapData) return [];
    const agg = new Map<string, { count: number, latencySum: number }>();
    data.mapData.forEach(m => {
      const existing = agg.get(m.country) || { count: 0, latencySum: 0 };
      existing.count += m.count;
      existing.latencySum += (m.latencySum || 0);
      agg.set(m.country, existing);
    });
    return Array.from(agg.entries())
      .map(([name, d]) => ({ 
        name, 
        count: d.count, 
        avg: d.count > 0 ? Math.round(d.latencySum / d.count) : 0 
      }))
      .sort((a,b) => b.count - a.count);
  }, [data?.mapData]);

  // Determine top 5 countries by request count for extra highlight
  const topCountriesSet = useMemo(() => {
    return new Set(countries.slice(0, 5).map(c => c.name));
  }, [countries]);

  const pageSize = 5;
  const paginatedCountries = countries.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.max(1, Math.ceil(countries.length / pageSize));

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Activity className="size-8 text-emerald-500 animate-pulse" />
      </div>
    );
  }

  const formatChartTime = (ts: number) => {
    const d = new Date(Number(ts));
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const colorScale = scaleLinear<string>()
    .domain([0, maxCount])
    .range([theme === "dark" ? "#2a2a2a" : "#e5e7eb", "#ff7f00"]);

  return (
    <div className={`min-h-screen font-sans p-6 lg:p-10 transition-colors ${theme === 'dark' ? 'dark bg-[#0b0e11] text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-[1200px] mx-auto border dark:border-white/10 border-gray-200 rounded-xl shadow-sm bg-white dark:bg-[#13171c] overflow-hidden transition-colors">
        
        {/* Header Filter Row */}
        <div className="flex justify-between items-center p-6 border-b dark:border-white/10 border-gray-200 bg-white dark:bg-[#13171c] transition-colors">
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 border dark:border-white/10 border-gray-200 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
              <Filter className="size-4" />
              Add filter
            </button>
            <button 
              onClick={toggleTheme}
              className="flex items-center justify-center border dark:border-white/10 border-gray-200 rounded-md w-8 h-8 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              {theme === 'dark' ? <Sun className="size-4 text-amber-400" /> : <Moon className="size-4 text-indigo-500" />}
            </button>
          </div>
          
          <div className="flex items-center gap-2 border border-gray-200 dark:border-white/10 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors cursor-pointer">
            <Calendar className="size-4 text-gray-500" />
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)} 
              className="bg-transparent outline-none cursor-pointer text-gray-700 dark:text-gray-300 dark:bg-[#13171c]"
            >
              <option value="1h">Last 1 hour</option>
              <option value="6hr">Last 6 hours</option>
              <option value="12hr">Last 12 hours</option>
              <option value="1 day">Last 24 hours</option>
              <option value="7 days">Last 7 days</option>
              <option value="30 days">Last 30 days</option>
            </select>
          </div>
        </div>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-b border-gray-200 dark:border-white/10 divide-x divide-gray-200 dark:divide-white/10">
          <div className="p-6 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <Activity className="size-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Total Requests</span>
            </div>
            <span className="text-2xl font-black">{data ? formatNumber(data.summary.total) : 0}</span>
          </div>
          <div className="p-6 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <Zap className="size-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Avg Latency</span>
            </div>
            <span className="text-2xl font-black">{data ? data.summary.avgResponseTime : 0} <span className="text-sm text-gray-400">ms</span></span>
          </div>
          <div className="p-6 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <Users className="size-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Active Users</span>
            </div>
            <span className="text-2xl font-black">{data?.activeUsers !== undefined ? formatNumber(data.activeUsers) : '0'}</span>
          </div>
          <div className="p-6 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <AlertCircle className="size-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Error Rate</span>
            </div>
            <span className="text-2xl font-black text-rose-500">{data?.errorRate !== undefined ? data.errorRate.toFixed(2) : '0.00'}%</span>
          </div>
        </div>

        {/* Geo Section */}
        <div className="p-6">
          <div className="flex justify-between items-end mb-6">
            <h2 className="text-xl font-bold tracking-tight">Requests volume by country</h2>
            <div className="text-gray-500 font-mono text-sm flex items-center gap-2">
              <span className="text-gray-400">Σ</span> 
              <span className="font-bold text-gray-700">{data ? formatNumber(data.summary.total) : 0} reqs</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Map Container */}
            <div className="bg-gray-100 dark:bg-[#0c0f12] rounded-xl overflow-hidden relative h-[380px] shadow-inner border border-gray-200 dark:border-white/5 transition-colors">
              {/* Zoom controls placeholder matching image */}
              <div className="absolute top-4 left-4 bg-gray-200/50 dark:bg-white/10 rounded-lg flex flex-col z-10 overflow-hidden backdrop-blur">
                <button className="p-2 text-gray-600 hover:bg-gray-300 dark:text-white/70 dark:hover:bg-white/20 dark:hover:text-white transition-colors border-b border-gray-300/50 dark:border-white/10">+</button>
                <button className="p-2 text-gray-600 hover:bg-gray-300 dark:text-white/70 dark:hover:bg-white/20 dark:hover:text-white transition-colors">-</button>
              </div>
              <ComposableMap projection="geoMercator" projectionConfig={{ scale: 110 }} style={{ width: "100%", height: "100%" }}>
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const d = data?.mapData?.find(m => m.country === geo.properties.name || m.country === geo.properties.iso_a2 || m.country === geo.properties.iso_a3);
                      const count = d ? d.count : 0;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          // Fill intensity based on request count
                          fill={count > 0 ? colorScale(count) : (theme === "dark" ? "#2a2a2a" : "#e5e7eb")}
                          // Highlight top countries with a distinct stroke
                          stroke={topCountriesSet.has(geo.properties.name) ? "#ff4500" : (theme === "dark" ? "#1a1a1a" : "#ffffff")}
                          strokeWidth={topCountriesSet.has(geo.properties.name) ? 1.5 : 0.5}
                          style={{
                            default: { outline: "none", transition: "all 250ms" },
                            hover: { fill: "#ff9800", outline: "none", transition: "all 250ms" },
                            pressed: { outline: "none" }
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </div>

            {/* Country List */}
            <div className="flex flex-col h-[380px]">
              <div className="flex-1 overflow-y-auto pr-2">
                {countries.length === 0 ? (
                  <div className="text-gray-400 text-sm h-full flex items-center justify-center">No geo data available for this range</div>
                ) : (
                  paginatedCountries.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors rounded-sm">
                      <span className="font-medium text-sm text-gray-800 dark:text-gray-200">{c.name}</span>
                      <div className="flex items-center gap-6">
                        <span className="font-bold text-sm text-gray-900 dark:text-gray-100">{formatNumber(c.count)}</span>
                        <span className="text-xs text-gray-400 font-mono w-16 text-right flex items-center justify-end gap-1">
                          <span className="text-[10px]">∅</span> {c.avg}ms
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Pagination */}
              {countries.length > 0 && (
                <div className="flex justify-end gap-1 mt-4 pt-2">
                  <button 
                    onClick={() => setPage(0)}
                    disabled={page === 0}
                    className="p-1.5 border border-gray-200 dark:border-white/10 rounded text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  ><ChevronsLeft className="size-4" /></button>
                  <button 
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-1.5 border border-gray-200 dark:border-white/10 rounded text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  ><ChevronLeft className="size-4" /></button>
                  <button 
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page === totalPages - 1}
                    className="p-1.5 border border-gray-200 dark:border-white/10 rounded text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  ><ChevronRight className="size-4" /></button>
                  <button 
                    onClick={() => setPage(totalPages - 1)}
                    disabled={page === totalPages - 1}
                    className="p-1.5 border border-gray-200 dark:border-white/10 rounded text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  ><ChevronsRight className="size-4" /></button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Requests per minute Chart */}
        <div className="border-t border-gray-200 dark:border-white/10 p-6 pt-8 bg-white dark:bg-[#13171c] transition-colors">
          <h2 className="text-xl font-bold mb-8 tracking-tight">Requests per minute</h2>
          <div className="h-[300px] w-full">
            {data?.historyBuckets && data.historyBuckets.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.historyBuckets} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorReqs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="ts" 
                    tickFormatter={formatChartTime} 
                    stroke="#9ca3af" 
                    fontSize={11} 
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    dy={10}
                    minTickGap={20}
                  />
                  <YAxis 
                    stroke="#9ca3af" 
                    fontSize={11} 
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => val === 0 ? '0 rpm' : `${val} rpm`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    labelFormatter={(ts) => formatChartTime(Number(ts))}
                    formatter={(val: any) => [`${val} reqs/min`, 'Volume']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="reqs" 
                    stroke="#2dd4bf" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorReqs)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No historical data available</div>
            )}
          </div>
        </div>

        {/* LLM Models Traffic */}
        <div className="border-t border-gray-200 dark:border-white/10 p-6 pt-8 bg-white dark:bg-[#13171c] transition-colors">
          <div className="flex items-center gap-2 mb-6">
            <Cpu className="size-5 text-indigo-500" />
            <h2 className="text-xl font-bold tracking-tight">LLM Models Traffic</h2>
          </div>
          <div className="h-[250px] w-full">
            {data?.modelsTraffic && data.modelsTraffic.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.modelsTraffic} margin={{ top: 10, right: 0, left: -20, bottom: 0 }} maxBarSize={50}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#374151' : '#e5e7eb'} />
                  <XAxis 
                    dataKey="model" 
                    stroke="#9ca3af" 
                    fontSize={11} 
                    tickLine={false}
                    axisLine={{ stroke: theme === 'dark' ? '#374151' : '#e5e7eb' }}
                    dy={10}
                  />
                  <YAxis 
                    stroke="#9ca3af" 
                    fontSize={11} 
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => formatNumber(val)}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: theme === 'dark' ? '#1f2937' : '#fff', border: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`, borderRadius: '6px', color: theme === 'dark' ? '#f3f4f6' : '#111827' }}
                    cursor={{ fill: theme === 'dark' ? '#374151' : '#f3f4f6', opacity: 0.4 }}
                  />
                  <Bar dataKey="reqs" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No models traffic data available</div>
            )}
          </div>
        </div>
        {/* Backend Analysis Batching */}
        <div className="border-t border-gray-200 dark:border-white/10 p-6 pt-8 bg-white dark:bg-[#13171c] transition-colors">
          <div className="flex items-center gap-2 mb-6">
            <Database className="size-5 text-emerald-500" />
            <h2 className="text-xl font-bold tracking-tight">Nursery DB Background Batching</h2>
            {data?.backendAnalysis?.isRunning && (
              <span className="ml-2 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-gray-200 dark:border-white/10 rounded-xl p-5 bg-gray-50 dark:bg-white/5 transition-colors">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Batch Cycles Completed</div>
              <div className="text-3xl font-black text-gray-900 dark:text-gray-100">
                {data?.backendAnalysis ? formatNumber(data.backendAnalysis.cycles) : 0}
              </div>
            </div>
            <div className="border border-gray-200 dark:border-white/10 rounded-xl p-5 bg-gray-50 dark:bg-white/5 transition-colors">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Tokens Analyzed (All-Time)</div>
              <div className="text-3xl font-black text-gray-900 dark:text-gray-100">
                {data?.backendAnalysis ? formatNumber(data.backendAnalysis.tokensAnalyzed) : 0}
              </div>
            </div>
            <div className="border border-gray-200 dark:border-white/10 rounded-xl p-5 bg-gray-50 dark:bg-white/5 transition-colors">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Current Batch Size</div>
              <div className="text-3xl font-black text-gray-900 dark:text-gray-100">
                {data?.backendAnalysis?.batchSize || 500}
              </div>
            </div>
          </div>
        </div>

        {/* Advanced API Analytics */}
        <div className="border-t border-gray-200 dark:border-white/10 p-6 pt-8 bg-white dark:bg-[#13171c] transition-colors">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="size-5 text-blue-500" />
            <h2 className="text-xl font-bold tracking-tight">API Analytics (Rate Limits & Usage)</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 font-medium">
                  <th className="pb-3 px-4 font-semibold text-xs tracking-wider uppercase">Target API</th>
                  <th className="pb-3 px-4 font-semibold text-xs tracking-wider uppercase text-right">Requests</th>
                  <th className="pb-3 px-4 font-semibold text-xs tracking-wider uppercase text-right">Avg Latency</th>
                  <th className="pb-3 px-4 font-semibold text-xs tracking-wider uppercase text-right">Errors</th>
                  <th className="pb-3 px-4 font-semibold text-xs tracking-wider uppercase text-right">Rate Limited (429)</th>
                  <th className="pb-3 px-4 font-semibold text-xs tracking-wider uppercase text-right">Unauthorized (401/403)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                {data?.advancedAnalytics?.length ? (
                  data.advancedAnalytics.map((api, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group">
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100 flex flex-col">
                        <span>{api.target}</span>
                        {api.topPaths?.length > 0 && (
                          <span className="text-[10px] text-gray-400 font-mono mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            Top: {api.topPaths[0]?.path} ({api.topPaths[0]?.reqs} reqs)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300 font-mono">{formatNumber(api.reqs)}</td>
                      <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300 font-mono">{api.avgLatency}ms</td>
                      <td className={`py-3 px-4 text-right font-mono font-medium ${api.errors > 0 ? 'text-rose-500' : 'text-gray-400 dark:text-gray-500'}`}>
                        {api.errors > 0 ? formatNumber(api.errors) : '-'}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono font-medium ${api.rateLimits > 0 ? 'text-amber-500' : 'text-gray-400 dark:text-gray-500'}`}>
                        {api.rateLimits > 0 ? formatNumber(api.rateLimits) : '-'}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono font-medium ${api.unauthorized > 0 ? 'text-rose-500' : 'text-gray-400 dark:text-gray-500'}`}>
                        {api.unauthorized > 0 ? formatNumber(api.unauthorized) : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400 text-sm">No API analytics data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
